import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, ArrowLeft, Mail, User, Lock, Globe, Sparkles, HelpCircle, Check, Loader2, X } from "lucide-react";
import { AccentButton, AuthScene, GoogleSignInButton, Input, Label, localeText } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT, useI18n } from "../i18n/I18nProvider";
import { register, checkSubdomain, googleConfig, ADMIN_URL, withLang, GOOGLE_REDIRECT_URL, type AccountUser, type BrandVoice, type GoogleAuthConfig, type ImportSkip, type SubdomainAvailability } from "../lib/auth";
import { stashOnboarding, clearStash } from "../lib/onboardingStash";
import { billing, type PublicPlan } from "../lib/billing";
import { fmtMoney } from "../lib/format";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";
import { ThemeToggle } from "../components/ThemeToggle";

type Cycle = "monthly" | "yearly";

export function SignupPage({
  onSuccess,
  onLoginClick,
  authSpec,
}: {
  onSuccess: (u: AccountUser, opts?: { importSiteId?: string }) => void;
  onLoginClick: () => void;
  authSpec?: AuthLayoutSpec;
}) {
  const t = useT();
  const { locale } = useI18n();
  const loc = locale === "et" ? "et" : "en";
  const glow = authSpec?.glow ?? "aurora";
  const [params] = useSearchParams();

  // Avalik signup-funnel: hinnaplaani-widget annab `?plan=<code>` (+ valikuline
  // `?cycle=`). Tasulise paketi korral suuname peale registreerimist Stripe'i.
  const planCode = (params.get("plan") ?? "").toLowerCase();
  const [cycle, setCycle] = useState<Cycle>(params.get("cycle") === "yearly" ? "yearly" : "monthly");
  const [plans, setPlans] = useState<PublicPlan[]>([]);

  // Avalik „Alusta"-funnel (web_site `/alusta`) võib eeltäita nime + alamdomeeni
  // (tee 1 „nullist"), kanda valitud valmiskujunduse (tee 2) VÕI skannitud vana
  // WP-saidi URL-i (tee 3) - mall/import rakendub esimesele saidile registreerimisel.
  const templateId = (params.get("template") ?? "").trim() || undefined;
  const importUrl = (params.get("import") ?? "").trim() || undefined;
  // Account-free editor claim: the editor's publish dialog sends a first-time visitor
  // here with `?draft=<id>&token=<token>` (an existing account still goes to the admin
  // login instead). Both halves are required - the token IS the capability, and an id
  // on its own opens nothing.
  const draftClaim = useMemo(() => {
    const id = (params.get("draft") ?? "").trim();
    const token = (params.get("token") ?? "").trim();
    return id && token ? { id, token } : undefined;
  }, [params]);
  // Onboarding (tee 1 + tee 2): `?onboard=1` = külastaja nägi capabilities-sammu;
  // `?caps=blog,forms` = valitud võimekused. `onboard` ilma `caps`-ita = jäta
  // vahele → `[]` (register talletab source=skipped). Ilma `onboard`-ita = ei
  // tulnud funneli capabilities-sammult → undefined (register ei talleta).
  const capabilities = useMemo<string[] | undefined>(() => {
    if (params.get("onboard") !== "1") return undefined;
    const raw = (params.get("caps") ?? "").trim();
    return raw ? raw.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 30) : [];
  }, [params]);
  // Onboarding brief (§5 Faas 2): vabateksti äri-kirjeldus. Register klassifitseerib
  // TAUSTAL → moodulid + sisu-toon. PÜÜTUD OLEKUSSE kohe mount'il (window.search'ist,
  // ENNE privaatsuse-puhastust) → immuunne URL-i muutusele. Ainult koos onboard=1.
  const [brief] = useState<string | undefined>(() => {
    if (typeof window === "undefined") return undefined;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("onboard") !== "1") return undefined;
    const raw = (sp.get("brief") ?? "").trim();
    return raw ? raw.slice(0, 400) : undefined;
  });
  // Tee 3 AI-intervjuu brändihääl (§7.2 samm 2) - funnel kannab query-parameetritena.
  // Enum'id valideeritakse (tundmatu väärtus → maha), et backend `.strict()` ei kaebaks.
  const brandVoice = useMemo<BrandVoice | undefined>(() => {
    if (!importUrl) return undefined;
    const tone = params.get("tone") ?? "";
    const cta = params.get("cta") ?? "";
    const emphasis = (params.get("emphasis") ?? "").trim();
    const bv: BrandVoice = {
      ...(["formal", "friendly", "salesy"].includes(tone) ? { tone: tone as BrandVoice["tone"] } : {}),
      ...(["call", "quote", "book", "contact"].includes(cta) ? { cta: cta as BrandVoice["cta"] } : {}),
      ...(emphasis ? { emphasis: emphasis.slice(0, 200) } : {}),
    };
    return bv.tone || bv.cta || bv.emphasis ? bv : undefined;
  }, [params, importUrl]);
  // Tee 3 valiku-accordion: funnel kannab MAHA võetud kirjed `skip`-parameetris
  // (JSON: { pages, posts, menu }). Vigane/puuduv → undefined = impordi kõik.
  const importSkip = useMemo<ImportSkip | undefined>(() => {
    if (!importUrl) return undefined;
    try {
      const raw = JSON.parse(params.get("skip") ?? "") as Partial<Record<"pages" | "posts" | "menu", unknown>>;
      const arr = (v: unknown): string[] =>
        Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && !!x.trim()).slice(0, 500) : [];
      const s = { pages: arr(raw.pages), posts: arr(raw.posts), menu: arr(raw.menu) };
      return s.pages.length || s.posts.length || s.menu.length ? s : undefined;
    } catch {
      return undefined;
    }
  }, [params, importUrl]);
  const [name, setName] = useState(params.get("name") ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [subdomain, setSubdomain] = useState(
    (params.get("subdomain") ?? "").toLowerCase().replace(/[^a-z0-9-]/g, ""),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  // Set only when a draft claim failed: the account exists, the design does not. Holds
  // the fresh site so the notice can still offer a one-click way into the builder.
  const [draftLost, setDraftLost] = useState<{ siteId: string; homePageId: string | null } | null>(null);
  const [googleCfg, setGoogleCfg] = useState<GoogleAuthConfig>({ enabled: false, clientId: "" });
  // Elav alamdomeeni saadavuse-kontroll (debounce'itud) - annab kasutajale kohese
  // tagasiside ENNE „Registreeru"-nuppu (broneeritud/karantiin/hõivatud). Server
  // jõustab lõpliku kontrolli, seega see on nõuandev (nupp ei blokeeru võistlusel).
  const [subAvail, setSubAvail] = useState<SubdomainAvailability | null>(null);
  const [subChecking, setSubChecking] = useState(false);

  // Privaatsus (§4): brief võib sisaldada isikuandmeid (nimi, asukoht, telefon) ja
  // query-string jõuab nginx/CF access-logidesse. Eemalda brief NÄHTAVAST URL-ist
  // kohe (enne võimalikku analytics-init'i) - väärtus on juba `brief`-olekusse püütud,
  // nii et register saab selle ikka kätte. Täislahendus (server-side stash-token) on §6.4.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("brief")) {
        url.searchParams.delete("brief");
        window.history.replaceState(window.history.state, "", url.toString());
      }
    } catch {
      /* eira */
    }
  }, []);

  // OAuth-stash (§6, (c-jääk)): Google-signup on account-first - kasutaja lahkub
  // siit Google'i juurde, mispeale funneli query-kontekst KAOB URL-ist. Talleta see
  // SAMA-päritolu sessionStorage'i, et maandumisel (main.tsx) esimese saidi loomisel
  // rakenduks. Mount-hetk = funneli väärtused (caps/brief pole siin muudetavad;
  // alamdomeen jääb admin/sites/new-s re-muudetavaks). E-posti tee kasutab URL-i otse
  // (register kohe) ja kustutab stash'i õnnestumisel → ei jää hiljem tarbimiseks alles.
  useEffect(() => {
    const rawSiteName = new URLSearchParams(window.location.search).get("siteName");
    stashOnboarding({
      template: templateId,
      subdomain: subdomain || undefined,
      siteName: rawSiteName ? rawSiteName.slice(0, 60) : undefined,
      onboard: capabilities !== undefined,
      caps: capabilities && capabilities.length ? capabilities.join(",") : undefined,
      brief,
      // Valitud pakett (tasuline → esimese saidi loomise järel Stripe checkout) -
      // e-posti tee loeb URL-ist otse, Google-tee vajab stash'i (query kaob ringil).
      plan: planCode || undefined,
      cycle: planCode ? cycle : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    googleConfig().then(setGoogleCfg).catch(() => {});
  }, []);

  // Debounce'itud saadavuse-kontroll: alamdomeeni muutusel oota 400 ms, siis päri
  // eelvaade. `cancelled`-lipp väldib aegunud vastuse kirjutamist (kiire tippimine).
  // Alla 3 tähemärgi (regex-miinimum) → tühjenda staatus, ära pärigi.
  useEffect(() => {
    const label = subdomain.trim();
    if (label.length < 3) {
      setSubAvail(null);
      setSubChecking(false);
      return;
    }
    let cancelled = false;
    setSubChecking(true);
    const timer = setTimeout(() => {
      checkSubdomain(label)
        .then((r) => {
          if (!cancelled) setSubAvail(r);
        })
        .finally(() => {
          if (!cancelled) setSubChecking(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [subdomain]);

  useEffect(() => {
    if (!planCode) return;
    billing.publicPlans().then(setPlans).catch(() => {});
  }, [planCode]);

  const selectedPlan = useMemo(() => plans.find((p) => p.code === planCode) ?? null, [plans, planCode]);
  // Tasuline = kuuhind > 0 (tasuta pakett / tundmatu kood → loome lihtsalt konto).
  const isPaid = Boolean(selectedPlan && selectedPlan.priceMonthlyCents > 0);
  const hasYearly = Boolean(selectedPlan && selectedPlan.priceYearlyCents > 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user, siteId, homePageId, draftClaim: claimResult } = await register(email, name, password, subdomain, {
        templateId,
        draft: draftClaim,
        importUrl,
        brandVoice,
        importSkip,
        capabilities,
        brief,
      });
      // The email path created the site immediately (from the URL context) -> clear the
      // OAuth stash so it cannot "wake up" on a later Google login in the same tab (a
      // safety net alongside its expiry).
      clearStash();
      // A draft that failed to claim: the account and the site exist, but the design the
      // visitor built does NOT. Stop here and say so rather than dropping them into an
      // empty builder wondering where their work went.
      if (draftClaim && claimResult && claimResult !== "applied") {
        setDraftLost({ siteId, homePageId });
        setBusy(false);
        return;
      }
      if (isPaid && selectedPlan) {
        try {
          const { url } = await billing.checkout(siteId, selectedPlan.code, cycle);
          if (url) {
            setRedirecting(true);
            window.location.href = url; // SPA → Stripe hostitud checkout
            return;
          }
        } catch {
          // Stripe seadistamata vms - konto on loodud, vii lihtsalt kontolehele.
        }
      }
      // Tee 1/2 (nullist / valmiskujundus): sait on valmis - vii värske kasutaja
      // OTSE builderisse avaleht lahti (mitte konto-hubi), et kujundust saaks kohe
      // muuta. Sessioon kehtib admin-hostil (küpsise Domain = baasdomeen).
      if (!importUrl) {
        setRedirecting(true);
        const path = homePageId ? `/pages/${encodeURIComponent(homePageId)}` : "/pages";
        window.location.href = withLang(`${ADMIN_URL}${path}?site=${encodeURIComponent(siteId)}`);
        return;
      }
      // Tee 3 (WP-import): konverteerimine käivitus registreerimisel taustal -
      // suuna „ehitame su saiti" progress-vaatesse (koondplaan §7.2 sammud 4-5).
      onSuccess(user, { importSiteId: siteId });
    } catch (err: any) {
      if (err.message === "email_taken") setError(t("signup.emailTaken"));
      else if (err.message === "subdomain_taken") setError(t("signup.subdomainTaken"));
      else if (err.message === "subdomain_reserved") setError(t("signup.subdomainReserved"));
      else if (err.message === "subdomain_quarantined") setError(t("signup.subdomainQuarantined"));
      else setError(t("signup.failed"));
      setBusy(false);
    }
  }

  const priceCents = cycle === "yearly" ? selectedPlan?.priceYearlyCents ?? 0 : selectedPlan?.priceMonthlyCents ?? 0;

  // Alamdomeeni elav-staatuse sõnum + toon (nõuandev; server jõustab lõpliku).
  const subStatus = useMemo<{ text: string; tone: "ok" | "bad" | "checking" } | null>(() => {
    if (subdomain.trim().length < 3) return null;
    if (subChecking) return { text: t("signup.subdomainChecking"), tone: "checking" };
    if (!subAvail) return null;
    if (subAvail.available) return { text: t("signup.subdomainAvailable"), tone: "ok" };
    const key =
      subAvail.reason === "taken"
        ? "signup.subdomainTaken"
        : subAvail.reason === "reserved"
          ? "signup.subdomainReserved"
          : subAvail.reason === "quarantined"
            ? "signup.subdomainQuarantined"
            : "signup.subdomainInvalid";
    return { text: t(key), tone: "bad" };
  }, [subdomain, subChecking, subAvail, t]);

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <LanguageMenu />
        </div>
      }
    >
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="card-aurora-border rounded-xl bg-card/95 p-6 shadow-soft">
            <div className="mb-1 flex items-center gap-3">
              <div className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Rocket className="size-5" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{t("signup.title")}</h1>
            </div>
            <p className="mb-6 text-sm text-muted-foreground">{t("signup.subtitle")}</p>

            {/* The visitor arrived from the account-free editor with work in hand. Say so:
                "create an account" reads very differently when it means "keep what I made". */}
            {draftClaim && !draftLost && (
              <div className="mb-6 flex items-start gap-2.5 rounded-lg border border-primary/25 bg-primary/5 p-4 text-sm">
                <Sparkles className="mt-0.5 size-4 shrink-0 text-primary" />
                <span>{t("signup.draftIntro")}</span>
              </div>
            )}

            {/* The claim failed after the account was created. The account is real, the
                design is gone - both halves have to be said out loud. */}
            {draftLost && (
              <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4">
                <p className="text-sm font-semibold">{t("signup.draftLostTitle")}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t("signup.draftLostBody")}</p>
                <AccentButton
                  glow={glow}
                  size="lg"
                  className="mt-3 w-full"
                  onClick={() => {
                    setRedirecting(true);
                    const path = draftLost.homePageId
                      ? `/pages/${encodeURIComponent(draftLost.homePageId)}`
                      : "/pages";
                    window.location.href = withLang(
                      `${ADMIN_URL}${path}?site=${encodeURIComponent(draftLost.siteId)}`,
                    );
                  }}
                >
                  {t("signup.draftLostCta")}
                </AccentButton>
              </div>
            )}

            {selectedPlan && (
              <div className="mb-6 rounded-lg border border-primary/25 bg-primary/5 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="size-4 text-primary" />
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t("signup.planLabel")}</div>
                      <div className="text-sm font-semibold">{localeText(selectedPlan.name, loc)}</div>
                    </div>
                  </div>
                  {isPaid && (
                    <div className="text-right">
                      <span className="text-lg font-bold">{fmtMoney(priceCents, selectedPlan.currency, locale)}</span>
                      <span className="text-xs text-muted-foreground">{cycle === "yearly" ? t("signup.perYear") : t("signup.perMonth")}</span>
                    </div>
                  )}
                </div>

                {isPaid && hasYearly && (
                  <div className="mt-3 inline-flex rounded-md border border-border p-0.5 text-xs">
                    {(["monthly", "yearly"] as Cycle[]).map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCycle(c)}
                        className={`rounded px-3 py-1 font-medium transition-colors ${cycle === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        {c === "monthly" ? t("signup.cycleMonthly") : t("signup.cycleYearly")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mb-4 p-3 text-sm text-destructive bg-destructive/10 rounded-md ring-1 ring-destructive/20 font-medium">
                {error}
              </div>
            )}

            {googleCfg.enabled && (
              <div className="mb-5">
                <GoogleSignInButton href={GOOGLE_REDIRECT_URL} label={t("login.google")} disabled={busy} />
                <div className="mt-5 flex items-center gap-3">
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">{t("login.or")}</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
              </div>
            )}

            <form onSubmit={submit} className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">{t("signup.name")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-9"
                    autoComplete="name"
                    placeholder={t("signup.namePlaceholder")}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">{t("signup.email")}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    autoComplete="email"
                    placeholder={t("signup.emailPlaceholder")}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">{t("signup.password")}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    autoComplete="new-password"
                    placeholder={t("signup.passwordPlaceholder")}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                {/* Domeeni-label + (?) abi-vihje: seleta et sait saab tasuta
                    husly.app-aadressi ning oma domeeni saab alates Standard-paketist
                    (alati saab hiljem mugavalt üle kolida). */}
                <div className="relative flex items-center gap-1.5">
                  <Label htmlFor="subdomain">{t("signup.subdomain")}</Label>
                  <button
                    type="button"
                    aria-label={t("signup.domainHelp")}
                    className="peer inline-flex text-muted-foreground transition-colors hover:text-foreground focus-visible:text-foreground focus:outline-none"
                  >
                    <HelpCircle className="size-3.5" />
                  </button>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute left-0 top-full z-20 mt-1.5 w-64 rounded-lg border border-border bg-card px-3 py-2 text-xs font-normal leading-relaxed text-muted-foreground opacity-0 shadow-soft transition-opacity duration-150 peer-hover:opacity-100 peer-focus-visible:opacity-100"
                  >
                    {t("signup.domainHelp")}
                  </span>
                </div>
                <div className="relative flex items-center">
                  <Globe className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="subdomain"
                    name="subdomain"
                    type="text"
                    required
                    minLength={3}
                    pattern="[a-z0-9\-]+"
                    title={t("signup.subdomainTitle")}
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                    className="pl-9 pr-[85px]"
                    placeholder={t("signup.subdomainPlaceholder")}
                    aria-invalid={subStatus?.tone === "bad" || undefined}
                    aria-describedby="signup-subdomain-status"
                  />
                  <div className="absolute right-3 text-muted-foreground text-sm pointer-events-none">
                    .husly.app
                  </div>
                </div>
                {subStatus && (
                  <p
                    id="signup-subdomain-status"
                    role={subStatus.tone === "bad" ? "alert" : "status"}
                    aria-live={subStatus.tone === "bad" ? "assertive" : "polite"}
                    className={`flex items-center gap-1.5 text-xs ${
                      subStatus.tone === "ok"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : subStatus.tone === "bad"
                          ? "text-destructive"
                          : "text-muted-foreground"
                    }`}
                  >
                    {subStatus.tone === "checking" ? (
                      <Loader2 className="size-3.5 shrink-0 animate-spin" />
                    ) : subStatus.tone === "ok" ? (
                      <Check className="size-3.5 shrink-0" />
                    ) : (
                      <X className="size-3.5 shrink-0" />
                    )}
                    <span>{subStatus.text}</span>
                  </p>
                )}
              </div>

              <AccentButton glow={glow} type="submit" size="md" loading={busy} className="mt-1 w-full">
                {redirecting ? t("signup.redirecting") : isPaid ? t("signup.submitPaid") : t("signup.submit")}
              </AccentButton>
            </form>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={onLoginClick}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors inline-flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("signup.backToLogin")}
              </button>
            </div>
          </div>
        </motion.div>
    </AuthScene>
  );
}
